<template>
  <div>
    <navbar :links="links" />
    <!-- Header -->
    <p class="title mt-4">
      {{ $t('contentCreatorBillingHeader') }}
    </p>
    <!-- Fullscreen loading indicator -->
    <fullpage-loading-indicator
      :loading="loading"
      blocking
    />
    <!-- Aggregated info -->
    <div v-if="bookings.length > 0">
      <v-layout wrap>
        <v-flex
          class="overall-box pa-2 text-xs-center"
          :class="{'bordered no-border-right': $vuetify.breakpoint.smAndUp}"
        >
          <div class="subheading mt-1 mb-2">
            {{ $t('contentCreatorBillingOverallBilledBookings') }}
          </div>
          <div class="title mb-2">
            {{ bookingsThatCostMoney.length }}
          </div>
        </v-flex>
        <v-flex
          class="overall-box pa-2 text-xs-center"
          :class="{'bordered no-border-right': $vuetify.breakpoint.smAndUp}"
        >
          <div class="subheading mt-1 mb-2">
            {{ $t('contentCreatorBillingOverallMonthlyCosts') }}
          </div>
          <div class="title mb-2">
            {{ overallCost }} €
          </div>
        </v-flex>
        <v-flex
          class="overall-box pa-2 text-xs-center"
          :class="{bordered: $vuetify.breakpoint.smAndUp}"
        >
          <div class="subheading mt-1 mb-2">
            {{ $t('contentCreatorBillingOverallDue') }}
          </div>
          <div class="title mb-2">
            {{ $t('contentCreatorBillingOverallDueFirstOfMonth') }}
          </div>
        </v-flex>
      </v-layout>
    </div>
    <!-- Bookings -->
    <div v-if="bookings.length === 0">
      {{ $t('contentCreatorBillingNoneActiveCurrently') }}.
    </div>
    <v-card
      v-for="(booking, bookingIndex) in bookings"
      :key="'booking_card_' + booking.id"
      flat
      class="my-3 bordered"
      :class="{'grow-1': $vuetify.breakpoint.xs}"
    >
      <v-card-title class="pt-2 pb-1">
        <v-layout align-center>
          <div class="ml-auto grey--text text--darken-2 caption">
            {{ $t('contentCreatorBookingBookedAt') }}
            {{ booking.booked_at | dayjs('DD.MM.YYYY') }}
            <span v-if="booking.booked_by_fullname">
              {{ $t('contentCreatorBookingBookedByShort') }}
              {{ booking.booked_by_fullname }}
            </span>
          </div>
        </v-layout>
      </v-card-title>
      <v-card-text class="pt-0 pb-2">
        <div class="mb-2">
          <div
            v-if="booking.workspace"
            class="body-2"
          >
            {{ booking.workspace.name }}
          </div>
          <div class="caption">
            {{ booking.plan.name }}
          </div>
        </div>
        <v-layout wrap>
          <!-- Quota -->
          <v-flex
            sm4
            xs12
          >
            <div class="mt-2 mb-2 body-2 grey--text text--darken-1">
              {{ $t('contentCreatorBillingQuota') }}
            </div>
            <div
              v-if="booking.plan.is_rate_limited"
              :class="{
                'warning--text': booking.quota_depleted,
              }"
              :title="$t('contentCreatorBookingQuotaTooltip')"
            >
              {{ $t('contentCreatorBookingQuotaUsed') }}:
              <div class="nowrap">
                {{
                  booking.quota_depleted
                    ? booking.plan.production_limit_per_month
                    : booking.plan.production_limit_per_month -
                      booking.quota_current_available
                }}
                / {{ booking.plan.production_limit_per_month }}
              </div>
              <div
                v-if="
                  booking.quota_current_available <
                    booking.plan.production_limit_per_month
                "
              >
                {{ $t('contentCreatorBookingQuotaNextAvailable') }}:
                <div>
                  {{ booking.quota_next_available_date | dayjs('DD.MM.YYYY') }}
                </div>
              </div>
            </div>
            <div v-else>
              {{ $t('toUnlimited') }}
            </div>
          </v-flex>
          <!-- Costs -->
          <v-flex
            sm5
            xs12
          >
            <div class="mt-2 mb-2 body-2 grey--text text--darken-1">
              {{ $t('contentCreatorBillingCosts') }}
            </div>
            <div v-if="booking.is_test">
              {{ $t('contentCreatorPlanTestBooking') }}
            </div>
            <div
              v-else
              style="overflow: auto"
            >
              <span v-if="booking.plan.is_billed_per_store">
                {{ $t('contentCreatorPlanPriceUnitPerStore') }}:
                <div class="nowrap body-2">
                  {{ booking.billed_stores.length }} x
                  {{ booking.plan.price_monthly | price }} =
                  {{
                    booking.billed_stores.length * booking.plan.price_monthly
                  }}
                  €
                </div>
                <div
                  class="clickable my-1 grey--text text--darken-2"
                  @click="toggleStoreListExpanded(booking)"
                >
                  <v-icon
                    v-if="!bookingIdToStoreListExpanded[booking.id]"
                    small
                    class="grey--text text--darken-2"
                  >
                    keyboard_arrow_down
                  </v-icon>
                  <v-icon
                    v-else
                    small
                    class="grey--text text--darken-2"
                  >
                    keyboard_arrow_up
                  </v-icon>
                  {{ $t('contentCreatorBookingExpandStores') }}
                </div>
                <v-slide-y-transition>
                  <div v-if="bookingIdToStoreListExpanded[booking.id]">
                    <div
                      v-for="store in booking.billed_stores"
                      :key="store.id"
                      class="nowrap mb-1"
                    >
                      <v-icon
                        small
                        color="msr"
                      > location_on </v-icon>
                      <b>{{ store.city }}</b>
                      <span>{{ store.name }}</span>
                      <span class="grey--text">{{ store.address }}</span>
                    </div>
                  </div>
                </v-slide-y-transition>
              </span>
              <span v-else>
                {{ $t(booking.plan.price_unit) }}:
                <div class="nowrap body-2">
                  {{ getBookingPrice(booking) | price }} €
                </div>
              </span>
            </div>
            <span
              v-if="!booking.is_test"
              class="grey--text text--darken-2 caption"
            >
              {{ $t('contentCreatorPlanBilling') }}:
              {{
                booking.billing_period == 'YEARLY'
                  ? $t('contentCreatorPlanBillingYearly')
                  : $t('contentCreatorPlanBillingMonthly')
              }}
            </span>
          </v-flex>
          <!-- Actions -->
          <v-flex
            sm3
            xs12
          >
            <div class="mt-2 mb-2 body-2 grey--text text--darken-1">
&nbsp;
            </div>
            <div
              :title="
                loading || !booking.user_can_cancel
                  ? $t('noPermission')
                  : $t('contentCreatorBillingCancelPlan')
              "
            >
              <v-btn
                v-if="!booking.is_test && !booking.cancelled_at"
                color="error"
                flat
                class="ma-0"
                round
                :disabled="loading || !booking.user_can_cancel"
                data-cy="contentcreator-cancel-plan-btn"
                @click="askCancelBooking(booking)"
              >
                {{ $t('contentCreatorBillingCancelPlan') }}
              </v-btn>
              <div v-if="!!booking.test_expires_at">
                <span v-if="!booking.test_has_expired && bookingIndex === 0">{{
                  $t('contentCreatorBookingTestExpiresAt')
                }}</span>
                <span v-else>{{
                  $t('contentCreatorBookingTestExpiredAt')
                }}</span>
                {{ booking.test_expires_at | dayjs('DD.MM.YYYY') }}
                <span
                  v-if="
                    !booking.test_has_expired &&
                      booking.test_expires_in_days > 0 &&
                      bookingIndex === 0
                  "
                  class="caption nowrap grey--text text--darken-3"
                >{{
                  $t('inNumDays').replace(
                    '{days}',
                    booking.test_expires_in_days
                  )
                }}</span>
              </div>
              <div v-if="!!booking.cancelled_at && !booking.is_test">
                {{ $t('contentCreatorBookingCancelledAt') }}
                {{ booking.cancelled_at | dayjs('DD.MM.YYYY') }}<br>
                <small>
                  {{ $t('contentCreatorBookingBilledUntil') }}
                  {{ booking.billing_period_end | dayjs('DD.MM.YYYY') }}
                </small>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FullpageLoadingIndicator from '@/components/FullpageLoadingIndicator';
import Navbar from '@/components/Navbar';

export default {
  components: {
    FullpageLoadingIndicator,
    Navbar,
  },
  data() {
    return {
      links: ['contentcreatorplans', 'contentcreatorbilling'],
      loading: false,
      bookingIdToStoreListExpanded: {},
    };
  },
  computed: {
    bookings() {
      return this.$store.state.common.contentcreatorBookings;
    },
    bookingsThatCostMoney() {
      // Overlapping billing periods are not yet considered here.
      return this.bookings.filter((booking) => {
        return booking.is_currently_billed;
      });
    },
    overallCost() {
      let overall = 0;
      for (const booking of this.bookingsThatCostMoney) {
        const price = this.getBookingPrice(booking);
        if (booking.plan.is_billed_per_store) {
          overall += Number(price) * booking.billed_stores.length;
        } else {
          overall += Number(price);
        }
      }
      return overall;
    },
  },
  created() {
    this.fetchContentCreatorPlanBookings();
  },
  methods: {
    getBookingPrice(booking) {
      if (booking.not_billed_and_managed_by_myshopradio) {
        return 0;
      }
      return booking.billing_period == 'YEARLY'
        ? booking.plan.price_yearly
        : booking.plan.price_monthly;
    },
    fetchContentCreatorPlanBookings(redirectToPlansWhenEmpty = false) {
      this.loading = true;
      const promise = this.$store.dispatch('LIST_CONTENTCREATOR_BOOKINGS');
      promise.finally(() => {
        this.loading = false;
        if (redirectToPlansWhenEmpty && this.bookings.length === 0) {
          this.$router.push({name: 'contentcreatorplans'});
        }
      });
      return promise;
    },
    toggleStoreListExpanded(booking) {
      const expanded = this.bookingIdToStoreListExpanded[booking.id];
      this.$set(this.bookingIdToStoreListExpanded, booking.id, !expanded);
    },
    askCancelBooking(booking) {
      const confirmed = confirm(this.$t('contentCreatorBookingCancelAsk'));
      if (confirmed) {
        this.cancelBooking(booking);
      }
    },
    cancelBooking(booking) {
      this.loading = true;
      this.$api
        .cancelContentCreatorPlanBooking(booking.id)
        .then(() => {
          // Fetch latest user.profile.content_creator_plan.
          this.$store.dispatch('RETRIEVE_CURRENT_USER');
          const redirectToPlansWhenEmpty = true;
          this.fetchContentCreatorPlanBookings(redirectToPlansWhenEmpty);

          this.$bus.$emit('alert-success', {
            message: 'contentCreatorBookingCancelled',
            translate: true,
          });
        })
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.overall-box {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/pages/ContentCreatorBilling.vue